import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GinoMainSectionIcon } from "../assets/icons/gino-icons";

interface WelcomeScreenProps {
  userName?: string;
  userRole?: string;
}

const CAPABILITY_ITEMS = [
  { icon: "💪", label: "Training Plans", desc: "Personalized plans based on your evaluation data" },
  { icon: "🍎", label: "Nutrition Plans", desc: "Meal plans tailored for football performance" },
  { icon: "⭐", label: "Talent Reports", desc: "AI-powered player evaluations with 27 criteria" },
  { icon: "📅", label: "Events & Coaches", desc: "Find training events, coaches, and programs" },
];

const WelcomeScreen = ({ userName, userRole }: WelcomeScreenProps) => {
  const { t } = useTranslation("gino", { keyPrefix: "welcomeScreen" });
  const capabilityItems = [
    { icon: "💪", label: t("capabilities.trainingPlans"), desc: t("capabilities.trainingPlansDescription") },
    { icon: "🍎", label: t("capabilities.nutritionPlans"), desc: t("capabilities.nutritionPlansDescription") },
    { icon: "⭐", label: t("capabilities.talentReports"), desc: t("capabilities.talentReportsDescription") },
    { icon: "📅", label: t("capabilities.eventsAndCoaches"), desc: t("capabilities.eventsAndCoachesDescription") },
  ];
  return (
  <Box sx={styles.container}>
    <GinoMainSectionIcon />
    <Typography sx={styles.title}>
      {userName ? <>{t("greetingWithName", { name: userName })} <span style={{ color: "#106048" }}>GINO</span></> : <>{t("greetingWithoutName")} <span style={{ color: "#106048" }}>GINO</span></>}
    </Typography>
    <Typography sx={styles.subtitle}>{t("subtitle")}</Typography>
    <Typography sx={styles.desc}>
      {userRole === "COACH"
        ? t("coachDescription")
        : t("defaultDescription")}
    </Typography>
    <Box sx={styles.capabilities}>
      {capabilityItems.map((item) => (
        <Box key={item.label} sx={styles.capItem}>
          <Box sx={styles.capIcon}><Typography sx={{ fontSize: 18 }}>{item.icon}</Typography></Box>
          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#111827", fontFamily: "Montserrat" }}>{item.label}</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280", fontFamily: "Montserrat", mt: "1px" }}>{item.desc}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
  );
};

export default WelcomeScreen;

const styles = {
  container: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", padding: "40px 20px", gap: "8px" },
  title: { fontSize: 28, fontWeight: 900, color: "#111827", fontFamily: "Montserrat", letterSpacing: "-0.02em" },
  subtitle: { fontSize: 15, fontWeight: 600, color: "#6b7280", fontFamily: "Montserrat" },
  desc: { fontSize: 13, color: "#9ca3af", maxWidth: 400, lineHeight: 1.6, fontFamily: "Montserrat" },
  capabilities: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px", mt: "20px", maxWidth: 500 },
  capItem: { display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px", background: "#fff", border: "1px solid #eef0f2", borderRadius: "14px", textAlign: "left" as const, boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -16px rgba(16,24,40,0.12)", transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease", cursor: "default", "&:hover": { transform: "translateY(-2px)", borderColor: "#d7ece0", boxShadow: "0 2px 4px rgba(16,24,40,0.05), 0 12px 28px -14px rgba(16,96,72,0.22)" } },
  capIcon: { width: 36, height: 36, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)" },
};
